/**
 * Pure helpers for the admin grant forms on Store details (MOT-681): the
 * "add every feature of a tier" picks on Grandfather extra features, and the
 * Stripe discount preview on the tier grant.
 */

export type TierLimits = Record<string, unknown>;

/** A feature tier as `GET /billing/admin/feature-tiers` returns it. */
export interface FeatureTierOption {
  tier: string;
  label: string;
  fullAccess?: boolean;
  price?: number | null;
  currency?: string | null;
  interval?: string | null;
  hasStripePrice?: boolean;
  limits?: TierLimits | null;
}

/** Ranked limits, lowest rung first. */
const RANKS = ['basic', 'advanced', 'complete'];

/** Limits a grandfather grant cannot give, named the way the card says them. */
const LEFT_OUT_LABELS: Record<string, string> = {
  maxUsers: 'users',
  maxIntegrations: 'integrations',
  maxApiCalls: 'API calls',
  reportLevel: 'sales reporting level',
  variablePricingLevel: 'variable pricing level',
  manufacturerReportLevel: 'manufacturer reporting level',
  mapAuditLevel: 'MAP audit level',
  aiContentTools: 'AI content tools',
  aiCopyMonthlyGenerations: 'AI copy generations',
  aiBulkMonthlyOperations: 'bulk AI operations',
};

/**
 * The grandfather-able features `tier` has and the store's own plan lacks, in
 * the order the select lists them.
 */
export function tierFeaturePick(
  tierLimits: TierLimits | null | undefined,
  ownLimits: TierLimits | null | undefined,
  grantableKeys: readonly string[]
): string[] {
  if (!tierLimits) return [];
  return grantableKeys.filter((key) => tierLimits[key] === true && ownLimits?.[key] !== true);
}

function exceeds(tierValue: unknown, ownValue: unknown): boolean {
  if (typeof tierValue === 'boolean') return tierValue && ownValue !== true;
  if (typeof tierValue === 'number') {
    const own = typeof ownValue === 'number' ? ownValue : 0;
    if (own === -1) return false;
    return tierValue === -1 || tierValue > own;
  }
  if (typeof tierValue === 'string') {
    const ownRank = RANKS.indexOf(typeof ownValue === 'string' ? ownValue : 'basic');
    return RANKS.indexOf(tierValue) > Math.max(ownRank, 0);
  }
  return false;
}

/**
 * What a tier has beyond the store's own plan that grandfathering cannot
 * hand over: counts, ranks and the AI switch that comes with its counts.
 */
export function tierLimitsLeftOut(
  tierLimits: TierLimits | null | undefined,
  ownLimits: TierLimits | null | undefined
): string[] {
  if (!tierLimits) return [];
  return Object.keys(LEFT_OUT_LABELS)
    .filter((key) => exceeds(tierLimits[key], ownLimits?.[key]))
    .map((key) => LEFT_OUT_LABELS[key]);
}

/** "a, b and c". */
export function joinWords(words: string[]): string {
  if (words.length <= 1) return words.join('');
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}

/** "$149/mo"; whole amounts without cents. */
export function formatIntervalPrice(amount: number, interval?: string | null, currency?: string | null): string {
  const value = Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
  const prefix = !currency || currency.toUpperCase() === 'USD' ? '$' : `${currency.toUpperCase()} $`;
  return `${prefix}${value}/${interval === 'yearly' ? 'yr' : 'mo'}`;
}

/**
 * Why a discounted price cannot be granted for `tier`, or null when it can.
 * The server checks the same rules; this keeps the button honest.
 */
export function discountPriceProblem(tier: FeatureTierOption | undefined, price: number | null): string | null {
  if (!tier || tier.fullAccess) return 'Pick a tier with a price.';
  if (typeof tier.price !== 'number' || tier.price <= 0) return `${tier.label} has no price on this stack.`;
  if (!tier.hasStripePrice) return `${tier.label} has no Stripe price linked.`;
  if (price === null || !Number.isFinite(price)) return 'Enter what they pay.';
  if (price < 0) return 'The price cannot be negative.';
  if (price >= tier.price) {
    return `Enter less than ${formatIntervalPrice(tier.price, tier.interval, tier.currency)}.`;
  }
  return null;
}
